/*=== CORE IMPORTS ===*/
import { html } from 'lit';

/*=== MIXIN CLASS DEFINITION ===*/

/**
 * # Record Actions Mixin Class (For Views)
 * 
 * Use this mixin to add functionality related to `allowedActions` to views that have context.
 * 
 * ## How to Use
 * 
 * ### Step 1 - Make sure actions are defined and imported properly
 * 
 * Make sure that the record / entity that you're working with is returning a context object that
 * contains an `allowedActions` array.
 * 
 * Then make sure that the actions are defined in a `*-client-actions.js` file in `/src/models/`.
 * 
 * Import the map version into your view.
 * 
 * Example:
 * ```javascript
 * import { guildMemberActionsMap } from '../../models/guild-member-client-actions.js';
 * ```
 * 
 * ### Step 2 - Add the mixin and any missing shoelace components
 * 
 * Add the mixin to your view class, and wrap your base class with it.
 * 
 * ```javascript
 * import { RecordActionsMixin } from '../../modules/record-actions-mixin.js';
 * 
 * // ...
 * 
 * export class GuildProfileView extends RecordActionsMixin(BaseGuildView) {
 * // ...
 * }
 * ```
 * 
 * You also need to make sure that the following shoelace components are imported:
 * - `<sl-button>`: For the action buttons.
 * - `<sl-icon>`: For the action button icons.
 * - `<sl-spinner>`: For the loading state of the action buttons.
 * - `<sl-dropdown>`: For the action menu items.
 * - `<sl-menu>`: For the action menu items.
 * - `<sl-menu-item>`: For the action menu item icons.
 * 
 * ### Step 3 - Implement the required methods
 * 
 * You need to implement the following methods:
 * - `_recordActionsMap`: This should return the action map for the record.
 * - `_allowedActions`: This should return the allowed actions for the current member.
 * - `handleAction`: This should implement the logic to handle each action.
 * 
 * You can also optionally implement the following:
 * - `_recordActionButtons_processing`: Returns true when action buttons are in a loading state.
 * 
 * Examples:
 * 
 * ```javascript
 * get _recordActionsMap() {
 *   return guildMemberActionsMap;
 * }
 *
 * get _allowedActions() {
 *   return this.guildMemberContext?.allowedActions || [];
 * }
 *
 * get _recordActionButtons_processing() {
 *   return this.processing;
 * }
 *
 * handleAction(actionKey) {
 *   switch (actionKey) {
 *     case 'edit':
 *       this.navigateTo(this.editProfilePath);
 *       break;
 *
 *     case 'deactivate':
 *       // Note: The confirmation is handled already by the mixin
 *       this.deactivateMembership();
 *       break;
 *
 *     default:
 *       console.error(`UNHANDLED action key: ${actionKey}`);
 *   }
 * }
 * ```
 * 
 * ### Step 4 - Add the action buttons/menu items templates to the view
 * 
 * Add the action buttons to your view using the `recordActionButtonsTemplate` getter and 
 * optionally making use of the `hasRecordActionButtons` or `recordActionButtons` getters.
 * 
 * Also add the action menu items using the `recordActionMenuTemplate` getter and
 * optionally making use of the `hasRecordActionMenuItems` or `recordActionMenuItems` getters.
 * 
 * Example:
 * 
 * ```javascript
 * // Inside of a render template method
 * ${!this.hasRecordActionButtons && !this.hasRecordActionMenuItems ? '' : html`
 *   <button-bar class="sl-themed-secondary">
 *     ${this.recordActionButtonsTemplate}
 *     ${this.recordActionMenuTemplate}
 *   </button-bar>
 * `}
 * ```
 * 
 * ### That's it!
 * 
 */
export const RecordActionsMixin = (SuperClass) => class extends SuperClass {

  /*=== OVERWRITEABLE METHODS ===*/

  /**
   * REQUIRED OVERWRITE. Overwrite this method to return the relevant action map. Ideally this 
   * should come from a `*-client-actions.js` file in `/src/models/`.
   * 
   * This will have a key for each potential action to be returned.
   * 
   * NOTE: The action map should ideally have an `index` number property for each action.
   * If not, overwrite `_recordActionsMapCompareFn` to sort the actions in a different way.
   */
  get _recordActionsMap() {
    return {
      edit: {
        value: 'edit',
        label: 'Edit',
        icon: 'edit',
        iconLibrary: 'material',
        isPrimary: false,
        displayInMenu: false,
        confirmationMessage: null,
        index: 0, // used to sort
      },
    };
  }

  /**
   * REQUIRED OVERWRITE. Overwrite this method to return an array of allowed actions for the
   * current member. This should usually be pulled from the context for this record. Each string
   * in the array should be a key in the `_recordActionsMap` object.
   */
  get _allowedActions() {
    return [];
  };

  /**
   * OPTIONAL OVERWRITE. This method is used to sort the record actions map when producing action
   * button / menu arrays.
   * 
   * By default it sorts by the `index` property of each action.
   * 
   * If your actions don't have an `index` property, you can overwrite this method to sort the 
   * actions in a different way.
   */
  _recordActionsMapCompareFn(a, b) {
    // Sort by index number
    return (a.index || 0) - (b.index || 0);
  }

  /**
   * OPTIONAL OVERWRITE. Overwrite this method to return true when the action buttons should be
   * disabled (and display a spinner) while the action is processing.
   */
  get _recordActionButtons_processing() {
    return false;
  }

  /**
   * REQUIRED OVERWRITE. Overwrite this method to implement the logic to handle each action.
   * 
   * This is called from `_handleRecordAction_base` AFTER checking the following abort conditions:
   * - Invalid action key
   * - Action not allowed
   * - Confirmation message: If there's a confirmation message, it will be shown and the action
   *   will be cancelled if the member clicks "Cancel".
   */
  handleAction(actionKey) {
    switch (actionKey) {
      case 'example':
        // Implement business logic here
        break;

      // Add more cases as needed for each action
      
      default:
        console.error(`UNHANDLED action key: ${actionKey}`);
    }
  }

  /*=== HANDLERS ===*/

  _handleRecordAction_base(event) {
    event.preventDefault();

    // Get the action key. This supports either a button click or an sl-menu event.
    const actionKey = event.detail?.item?.value || event.target?.value;
    const actionSettings = this._recordActionsMap[actionKey];
    const confirmationMessage = actionSettings?.confirmationMessage;

    // ES1: Action isn't properly configured
    if (!actionKey || !actionSettings) {
      console.error(`INVALID action key: ${actionKey}`);
      return null;
    }

    // ES2: Action not allowed
    if (!(this._allowedActions || []).includes(actionKey)) {
      console.error(`Action not allowed: ${actionKey}`);
      return null;
    }

    // Error states are clear! Confirm with member if needed.
    if (confirmationMessage && !confirm(confirmationMessage)) {
      console.log(`Action cancelled by member: ${actionKey}`);
      return null;
    }

    // Now we're ready to perform the action
    this.handleAction(actionKey);
  }

  /*=== GETTERS ===*/

  /**
   * You can use this to check if there are any action buttons to display.
   * 
   * @returns {boolean} True if there are action buttons to display, false otherwise.
   */
  get hasRecordActionButtons() {
    return this.recordActionButtons?.length > 0;
  }
  
  /**
   * You can use this to check if there are any action menu items to display.
   * 
   * @returns {boolean} True if there are action menu items to display, false otherwise.
   */
  get hasRecordActionMenuItems() {
    return this.recordActionMenuItems?.length > 0;
  }
  
  /**
   * This is used by the `recordActionButtonsTemplate` to render the buttons.
   * 
   * You shouldn't need to use this directly, but it returns an array of the allowed actions
   * for the current member filtering out the ones that are supposed to display in the menu.
   */
  get recordActionButtons() {
    if (!Array.isArray(this._allowedActions)) return [];

    return this._allowedActions
      .map((actionKey) => this._recordActionsMap[actionKey])
      .filter((action) => action && !action.displayInMenu)
      .sort(this._recordActionsMapCompareFn);
  }

  /**
   * This is used by the `recordActionMenuTemplate` to render the menu items.
   * 
   * You shouldn't need to use this directly, but it returns an array of the allowed actions
   * which are supposed to display in the menu.
   */
  get recordActionMenuItems() {
    if (!Array.isArray(this._allowedActions)) return [];

    return this._allowedActions
      .map((actionKey) => this._recordActionsMap[actionKey])
      .filter((action) => action && action.displayInMenu)
      .sort(this._recordActionsMapCompareFn);
  }

  /*=== TEMPLATES ===*/

  get recordActionButtonsTemplate() {
    if (!this.hasRecordActionButtons) return null;

    return this.recordActionButtons.map(({ label, icon, iconLibrary, value, isPrimary }) => html`
      <sl-button
        variant=${isPrimary ? 'primary' : 'default'}
        @click=${this._handleRecordAction_base}
        value=${value}
        ?disabled=${this._recordActionButtons_processing}
      >
        ${this._recordActionButtons_processing ? html`
          <sl-spinner slot="prefix"></sl-spinner>
          Processing...
        ` : html`
          <sl-icon slot="prefix" library=${iconLibrary} name=${icon}></sl-icon>
          ${label}
        `}
      </sl-button>
    `);
  }

  get recordActionMenuTemplate() {
    if (!this.hasRecordActionMenuItems) return null;

    return html`
      <sl-dropdown distance="8" hoist class="record-actions-menu">
        <sl-icon
          slot="trigger"
          library="material"
          name="settings"
          label="Actions"
        ></sl-icon>

        <sl-menu @sl-select=${this._handleRecordAction_base}>
          ${this.recordActionMenuItems.map(({ label, icon, iconLibrary, value }) => html`
            <sl-menu-item
              value=${value}
              @click=${this._handleRecordAction_base}
            >
              <sl-icon slot="prefix" library=${iconLibrary} name=${icon}></sl-icon>
              ${label}
            </sl-menu-item>
          `)}
        </sl-menu>
      </sl-dropdown>
    `;
  }

}
